import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';

import { useCreateShopCommissionMutation } from '../store/shop-commissions-api';
import { useGetShopCommissions } from './use-get-shop-commissions';
import { useShowResponseStatus } from '@/shared';
import { useGetShops } from '@/modules/shops';
import { useGetPaymentMethods } from '@/modules/payment-methods';
import { convertMethodsToTable } from '@/modules/payment-methods/utils/helpers/converter';

import type { TShopCommissionCreateParams } from '../store/types';
import type { TPaymentMethod } from '@/modules/payment-methods';

export type FormData = Omit<TShopCommissionCreateParams, 'shopId' | 'paymentMethodId'> & {
  shopId: string;
  paymentMethodId: string;
};

export const useCreateShopCommission = () => {
  const [createShopCommission, { isLoading: isCreateShopCommissionLoading, isSuccess, isError }] = useCreateShopCommissionMutation();

  const {
    items: commissions,
    isLoading: isGetCommissionsLoading,
  } = useGetShopCommissions({ pageSize: 50 });

  const {
    data: shops,
    isLoading: isShopsLoading,
  } = useGetShops('regular');

  const {
    data: paymentMethods,
    isLoading: isPaymentMethodsLoading,
  } = useGetPaymentMethods();

  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [avalablePaymentMethods, setAvalablePaymentMethods] = useState<TPaymentMethod[]>(paymentMethods ?? []);
  const isLoading = isShopsLoading || isPaymentMethodsLoading || isGetCommissionsLoading || isCreateShopCommissionLoading;

  useShowResponseStatus({
    isSuccess,
    successMessage: 'Комиссия успешно создана',
    isError,
    errorMassage: 'При создании комиссии произошла ошибка',
  });

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    // initialValues: {},
    validate: {
      shopId: (value) => !value ? 'Выберите ID магазина' : null,
      paymentMethodId: (value) => !value ? 'Выберите ID платёжного метода' : null,
      refillPercent: (value) => value !== 0 && !value ? 'Введите процент пополнения' : null,
    },
  });

  form.watch('shopId', ({ value }) => {
    setSelectedShopId(value);
  });

  useEffect(() => {
    const changedAvalablePaymentMethods: TPaymentMethod[] = convertMethodsToTable(paymentMethods ?? []).filter((method) => {
      const selectedCommissions = commissions.filter((commitssion) => commitssion.shopId.toString() === selectedShopId);
      const excludedMethodIdList = selectedCommissions.filter((commission) => commission.paymentMethodId === method.id).map((commission) => commission.paymentMethodId);
      const isHideMethod = excludedMethodIdList.includes(method.id);
      return !isHideMethod;
    }) ?? [];

    setAvalablePaymentMethods(changedAvalablePaymentMethods);

    if (!selectedShopId) {
      setAvalablePaymentMethods([]);
    }
  }, [selectedShopId]);

  const handleShopCommissionCreate = async (params: TShopCommissionCreateParams) => {
    await createShopCommission(params);
  };

  const handleSubmit = (values: FormData, onClose: () => void) => {
    const params: TShopCommissionCreateParams = {
      ...values,
      shopId: Number(values.shopId),
      paymentMethodId: Number(values.paymentMethodId),
    };

    handleShopCommissionCreate(params);
    onClose();
  };


  return {
    form,
    selectedShopId,
    shops,
    paymentMethods: avalablePaymentMethods,
    isLoading,
    onSubmit: handleSubmit,
    createShopCommission,
    handleShopCommissionCreate,
  };
};
